import { FilterGroupOption } from "@components/common/data-grid/adm-data-grid-types";
import { BulkActions } from "@components/common/data-grid/toolbar/filter-bulk-actions";
import {
  ArrowDownIcon,
  ChevronUpIcon,
  Chip,
  FilterMenuContent,
  Menu,
  Option,
  Text,
} from "@flat-ui";
import { Box, Stack } from "@mui-wrapper";
import { useState } from "react";

/**
 * Chips shown for the selection, the rest is summarized as a counter. Without a limit the chips
 * wrap onto new lines and the dropdown, and everything below it, moves with every option picked.
 */
const MAX_VISIBLE_CHIPS = 3;

interface Props {
  /** Label shown while nothing is selected, replaced by the chips afterwards */
  label: string;

  /** The options offered inside the dropdown */
  options: FilterGroupOption[];

  /** Ids of the options currently selected */
  selectedIds: string[];

  /** Called with the next selection whenever it changes */
  onChange: (selectedIds: string[]) => void;

  /** Whether to offer the actions that select every option at once, or deselect them all again */
  hasSelectAll?: boolean;
}

/**
 * @param option the option to list
 * @returns the option as a chip when it carries a color, as plain text otherwise
 */
function renderOptionLabel({
  label,
  chipColor,
}: FilterGroupOption): React.JSX.Element {
  if (!chipColor) {
    return <Text variant="bodyM">{label}</Text>;
  }

  return <Chip label={label} color={chipColor} textTransform="none" />;
}

/**
 * @param options every option of the dropdown, those of a group next to each other
 * @returns the options as the menu lists them, each group headed once above its first option
 */
function toMenuOptions(options: FilterGroupOption[]): Option[] {
  return options.flatMap((option, index) => {
    const menuOption: Option = {
      key: option.id,
      value: option.id,
      label: (
        <Stack sx={{ alignItems: "flex-start" }}>
          {renderOptionLabel(option)}
          {option.subtitle ? (
            <Text variant="helpText">{option.subtitle}</Text>
          ) : null}
        </Stack>
      ),
    };

    const { groupLabel } = option;
    const isGroupStart =
      groupLabel !== undefined && groupLabel !== options[index - 1]?.groupLabel;

    if (!isGroupStart) {
      return menuOption;
    }

    return [
      {
        key: `group-of-${option.id}`,
        value: groupLabel,
        label: groupLabel,
        isHeader: true,
      },
      menuOption,
    ];
  });
}

/**
 * A multi-select dropdown that shows a placeholder until options are selected, then replaces it with
 * the same removable, colored chips the plans table uses.
 *
 * @returns the dropdown for one filter group
 */
export function FilterDropdown({
  label,
  options,
  selectedIds,
  onChange,
  hasSelectAll = false,
}: Props): React.JSX.Element {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const menuOptions = toMenuOptions(options);

  function optionOf(optionId: string): FilterGroupOption | undefined {
    return options.find((option) => option.id === optionId);
  }

  function openMenu(event: React.SyntheticEvent<HTMLElement>): void {
    setAnchorElement(event.currentTarget);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>): void {
    if (["Enter", " ", "ArrowDown"].includes(event.key)) {
      event.preventDefault();
      openMenu(event);
    }
  }

  function removeOption(optionId: string): void {
    onChange(selectedIds.filter((selectedId) => selectedId !== optionId));
  }

  const hasBulkActions = hasSelectAll && options.length > 0;
  const hasSelection = selectedIds.length > 0;
  const visibleIds = selectedIds.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = selectedIds.length - visibleIds.length;

  return (
    <>
      <Box
        role="combobox"
        tabIndex={0}
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={anchorElement !== null}
        onClick={openMenu}
        onKeyDown={handleKeyDown}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minWidth: "9rem",
          minHeight: "2.5rem",
          px: 1.5,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          cursor: "pointer",
          "&:hover": { borderColor: "text.primary" },
        }}
      >
        {hasSelection ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {visibleIds.map((optionId) => {
              const option = optionOf(optionId);
              return (
                // Stop the click from bubbling up so removing a chip does not open the menu
                <Box
                  key={optionId}
                  component="span"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <Chip
                    label={option?.label ?? optionId}
                    color={option?.chipColor}
                    textTransform="none"
                    onDelete={() => removeOption(optionId)}
                  />
                </Box>
              );
            })}

            {hiddenCount > 0 && (
              <Text variant="bodyM" sx={{ whiteSpace: "nowrap" }}>
                +{hiddenCount}
              </Text>
            )}
          </Box>
        ) : (
          <Text variant="placeholder">{label}</Text>
        )}

        {/* The arrow of an accordion: down while the menu is closed, up while it is open */}
        {anchorElement ? <ChevronUpIcon /> : <ArrowDownIcon />}
      </Box>

      <Menu
        open={anchorElement !== null}
        anchorEl={anchorElement}
        onClose={() => setAnchorElement(null)}
        // The menu is wider than the trigger, so it grows away from the neighboring filter
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <FilterMenuContent
          options={menuOptions}
          selectedOptions={menuOptions.filter(
            ({ key, isHeader }) => !isHeader && selectedIds.includes(key)
          )}
          onFilterChange={(selectedOptions) =>
            onChange(selectedOptions.map(({ key }) => key))
          }
          showSearch={false}
          showClearAll={false}
        >
          {hasBulkActions && (
            <BulkActions
              isEverythingSelected={options.every(({ id }) =>
                selectedIds.includes(id)
              )}
              isNothingSelected={!hasSelection}
              onSelectAll={() => onChange(options.map(({ id }) => id))}
              onDeselectAll={() => onChange([])}
            />
          )}
        </FilterMenuContent>
      </Menu>
    </>
  );
}
